import { NgIf } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AlertFacade } from '@curbnturf/alert';
import { BoondockFacade } from '@curbnturf/boondock-listing/src/lib/+state/boondock.facade';
import {
  activityFormBuilder,
  amenityFormBuilder,
  CheckedComponent,
  extraFormBuilder,
  formatAmenityFormValue,
  formatPrivacyFormValue,
  formatSignalReceptionFormValue,
  formatSiteExtraFormValue,
  formatVehiclesFormValue,
  IActivityForm,
  IAmenityForm,
  IListingExtraForm,
  INoiseForm,
  IPhotoForm,
  IPrivacyForm,
  ISignalReceptionForm,
  IVehiclesForm,
  ListingForm,
  privacyFormBuilder,
  signalReceptionFormBuilder,
  ToggleComponent,
  vehiclesFormBuilder,
} from '@curbnturf/form-helpers';
import { siteComplete } from '@curbnturf/helpers';
import {
  BoondockAccommodationsComponent,
  ListingAccommodationsComponent,
  ListingAmenitiesComponent,
  ListingNoiseComponent,
  ListingRvsComponent,
  OnsiteActivitiesComponent,
  PrivacyFormComponent,
} from '@curbnturf/host-forms';
import {
  IActivity,
  IActivityType,
  IAmenity,
  IAmenityType,
  IBoondock,
  IListing,
  INoise,
  IPrivacy,
  ISite,
  IVehicle,
} from '@curbnturf/objects';
import { SiteFacade } from '@curbnturf/site';
import { IonicModule } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';

@Component({
  selector: 'curbnturf-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.scss'],
  standalone: true,
  imports: [
    BoondockAccommodationsComponent,
    CheckedComponent,
    FormsModule,
    IonicModule,
    ListingAccommodationsComponent,
    ListingAmenitiesComponent,
    ListingNoiseComponent,
    ListingRvsComponent,
    NgIf,
    OnsiteActivitiesComponent,
    PrivacyFormComponent,
    ReactiveFormsModule,
    ToggleComponent,
  ],
})
export class OverviewComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('amenitiesComponent')
  amenitiesComponent: ListingAmenitiesComponent;

  @ViewChild('privacyFormComponent')
  privacyFormComponent: PrivacyFormComponent;

  @ViewChild('listingNoiseComponent')
  listingNoiseComponent: ListingNoiseComponent;

  @ViewChild('accommodationComponent')
  listingAccommodationsComponent: ListingAccommodationsComponent;

  @Input() site: ISite;
  @Input() listing: IListing;
  @Input() boondock: IBoondock;

  siteComplete = siteComplete;

  subscriptions: Subscription[] = [];
  formSubscriptions: Subscription[] = [];

  accommodationsForm: FormGroup<{
    amenities: FormArray<FormGroup<IAmenityForm>>;
    maxHeight: FormControl<number | null>;
    maxWidth: FormControl<number | null>;
    maxLength: FormControl<number | null>;
    maxGuests: FormControl<number | null>;
  }>;

  boondockAccommodationsForm: FormGroup<{
    amenities: FormArray<FormGroup<IAmenityForm>>;
    sizeBuses: FormControl<number | null>;
    sizeCars: FormControl<number | null>;
  }>;

  activitiesForm: FormArray<FormGroup<IActivityForm>>;

  rvAccessForm: FormGroup<{
    vehicles: FormGroup<IVehiclesForm>;
    tentOnly: FormControl<boolean | null>;
  }>;

  amenitiesForm: FormGroup<{
    amenities: FormArray<FormGroup<IAmenityForm>>;
    signalReception: FormArray<FormGroup<ISignalReceptionForm>>;
    extras: FormArray<FormGroup<IListingExtraForm>>;
  }>;

  get sites(): ISite[] {
    if (this.listing?.sites) {
      return this.listing.sites;
    }
    return [];
  }

  get activities() {
    if (this.activitiesForm) {
      return this.activitiesForm;
    }

    return;
  }

  get amenities(): IAmenity[] {
    return [
      ...(this.amenitiesForm.value.amenities?.map((el): IAmenity => {
        return {
          id: el.id ?? undefined,
          boondock: undefined,
          site: undefined,
          description: el.description ?? undefined,
          type: el.type as IAmenityType['type'],
          custom: el.custom ?? undefined,
          options: el.options ?? undefined,
        };
      }) ?? []),
      ...(this.accommodationsForm.value.amenities?.map((el): IAmenity => {
        return {
          id: el.id ?? undefined,
          boondock: undefined,
          site: undefined,
          description: el.description ?? undefined,
          type: el.type as IAmenityType['type'],
          custom: el.custom ?? undefined,
          options: el.options ?? undefined,
        };
      }) ?? []),
      ...(this.boondockAccommodationsForm.value.amenities?.map((el): IAmenity => {
        return {
          id: el.id ?? undefined,
          boondock: undefined,
          site: undefined,
          description: el.description ?? undefined,
          type: el.type as IAmenityType['type'],
          custom: el.custom ?? undefined,
          options: el.options ?? undefined,
        };
      }) ?? []),
    ];
  }

  get extras() {
    if (this.amenitiesForm) {
      return this.amenitiesForm.controls.extras;
    }

    return;
  }

  noiseForm: FormGroup<INoiseForm>;

  get noise(): INoise {
    return {
      level: this.noiseForm.value.level ?? undefined,
      description: this.noiseForm.value.description ?? '',
    };
  }

  privacyForm: FormGroup<IPrivacyForm>;

  get privacy(): IPrivacy {
    return {
      secluded: !!this.privacyForm.value.secluded,
      home: !!this.privacyForm.value.home,
      others: !!this.privacyForm.value.others,
      neighbor: !!this.privacyForm.value.neighbor,
    };
  }

  get signalReception() {
    if (this.amenitiesForm) {
      return this.amenitiesForm.controls.signalReception;
    }

    return;
  }

  get tentOnly() {
    if (this.rvAccessForm) {
      return this.rvAccessForm.controls.tentOnly;
    }

    return;
  }

  get vehicles(): IVehicle {
    return {
      travelTrailer: !!this.rvAccessForm.value.vehicles?.travelTrailer,
      truckCamper: !!this.rvAccessForm.value.vehicles?.truckCamper,
      teardrop: !!this.rvAccessForm.value.vehicles?.teardrop,
      popup: !!this.rvAccessForm.value.vehicles?.popup,
      fifthWheel: !!this.rvAccessForm.value.vehicles?.fifthWheel,
      classA: !!this.rvAccessForm.value.vehicles?.classA,
      classB: !!this.rvAccessForm.value.vehicles?.classB,
      classC: !!this.rvAccessForm.value.vehicles?.classC,
    };
  }

  constructor(
    private fb: FormBuilder,
    private siteFacade: SiteFacade,
    private alertFacade: AlertFacade,
    private boondockFacade: BoondockFacade,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.createForms();
  }

  ngOnInit() {
    this.createForms();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
    this.clearFormSubscriptions();
  }

  clearFormSubscriptions() {
    this.formSubscriptions.forEach((sub) => sub.unsubscribe());
    this.formSubscriptions = [];
  }

  createForms() {
    this.clearFormSubscriptions();
    // Build the Forms
    this.privacyForm = privacyFormBuilder(this.fb);

    this.noiseForm = this.fb.group({
      level: new FormControl<'silence' | 'nature' | 'periodic' | 'constant' | null>(null),
      description: new FormControl<string | null>(null),
    });

    this.accommodationsForm = this.fb.group({
      amenities: this.fb.array([amenityFormBuilder(this.fb)]),
      maxHeight: new FormControl<number | null>(null),
      maxWidth: new FormControl<number | null>(null),
      maxLength: new FormControl<number | null>(null),
      maxGuests: new FormControl<number | null>(null),
    });

    this.boondockAccommodationsForm = this.fb.group({
      amenities: this.fb.array([amenityFormBuilder(this.fb)]),
      sizeBuses: new FormControl<number | null>(null),
      sizeCars: new FormControl<number | null>(null),
    });

    this.rvAccessForm = this.fb.group({
      vehicles: vehiclesFormBuilder(this.fb),
      tentOnly: new FormControl<boolean | null>(false),
    });

    this.amenitiesForm = this.fb.group({
      amenities: this.fb.array<FormGroup<IAmenityForm>>([], { updateOn: 'change' }),
      extras: this.fb.array<FormGroup<IListingExtraForm>>([], { updateOn: 'blur' }),
      signalReception: this.fb.array<FormGroup<ISignalReceptionForm>>([], { updateOn: 'change' }),
    });

    this.activitiesForm = this.fb.array([
      this.fb.group<IActivityForm>({
        id: new FormControl<number | null>(null),
        type: new FormControl<IActivityType['type'] | null>(null),
        typeCustom: new FormControl<string | null>(null),
        name: new FormControl<string | null>(null),
        description: new FormControl<string | null>(null),
        address: this.fb.group({
          address: new FormControl<string | null>(null),
          city: new FormControl<string | null>(null),
          state: new FormControl<string | null>(null),
          zipcode: new FormControl<string | null>(null),
        }),
        coordinates: this.fb.group({
          lat: new FormControl<number | null>(null),
          lon: new FormControl<number | null>(null),
        }),
        photos: this.fb.array<FormGroup<IPhotoForm>>([], { updateOn: 'blur' }),
        url: new FormControl<string | null>(null),
      }),
    ]);

    this.populateForms();

    this.formSubscriptions.push(
      this.privacyForm.valueChanges
        .pipe(
          tap(() => {
            this.privacyForm?.markAsDirty();
            this.privacyForm?.markAsTouched();
          }),
          debounceTime(1000),
        )
        .subscribe(() => this.saveNoiseAndPrivacy()),
      this.noiseForm.valueChanges
        .pipe(
          tap(() => {
            this.noiseForm?.markAsTouched();
            this.noiseForm?.markAsDirty();
          }),
          debounceTime(1000),
        )
        .subscribe(() => this.saveNoiseAndPrivacy()),
      this.rvAccessForm.valueChanges
        .pipe(
          tap(() => {
            this.rvAccessForm.markAsTouched();
            this.rvAccessForm.markAsDirty();
          }),
          debounceTime(1000),
        )
        .subscribe(() => this.saveRvAccess()),
      this.accommodationsForm.valueChanges
        .pipe(
          tap(() => {
            this.accommodationsForm.markAsTouched();
            this.accommodationsForm.markAsDirty();
          }),
          debounceTime(1000),
        )
        .subscribe(() => this.saveAccommodations()),
      this.boondockAccommodationsForm.valueChanges
        .pipe(
          tap(() => {
            this.boondockAccommodationsForm.markAsTouched();
            this.boondockAccommodationsForm.markAsDirty();
          }),
          debounceTime(1000),
        )
        .subscribe(() => this.saveBoondockAccommodations()),
      this.amenitiesForm.valueChanges
        .pipe(
          tap(() => {
            this.amenitiesForm.markAsTouched();
            this.amenitiesForm.markAsDirty();
          }),
          debounceTime(1000),
        )
        .subscribe(() => this.saveAmenities()),
      this.activitiesForm.valueChanges.pipe(debounceTime(1000)).subscribe(() => this.saveActivities()),
    );
  }

  populateForms() {
    // Populate the Forms
    if (this.site) {
      this.privacyForm.patchValue({
        ...this.site.privacy,
      });

      this.noiseForm.patchValue({
        ...this.site.noise,
      });

      this.accommodationsForm.patchValue({
        amenities: [],
        maxHeight: this.site.maxHeight,
        maxWidth: this.site.maxWidth,
        maxLength: this.site.maxLength,
        maxGuests: this.site.maxGuests,
      });

      this.rvAccessForm.patchValue({
        vehicles: this.site.vehicles,
        tentOnly: this.site.tentOnly,
      });

      this.amenitiesForm.controls.amenities.clear();
      this.site.amenities?.forEach((amenity) => {
        this.amenitiesForm.controls.amenities.push(amenityFormBuilder(this.fb, amenity));
        this.accommodationsForm.controls.amenities.push(amenityFormBuilder(this.fb, amenity));
      });

      this.activitiesForm.clear();
      this.site.activities?.forEach((activity) => {
        this.activitiesForm.push(activityFormBuilder(this.fb, activity));
      });
    }

    if (this.listing) {
      this.amenitiesForm.controls.signalReception.clear();
      this.listing.signalReception?.forEach((signal) => {
        this.amenitiesForm.controls.signalReception.push(signalReceptionFormBuilder(this.fb, signal));
      });

      this.amenitiesForm.controls.extras.clear();
      this.listing.extras?.forEach((extra) => {
        this.amenitiesForm.controls.extras.push(extraFormBuilder(this.fb, extra));
      });
    }

    if (this.boondock) {
      this.privacyForm.patchValue({
        ...this.boondock.privacy,
      });

      this.noiseForm.patchValue({
        ...this.boondock.noise,
      });

      this.boondockAccommodationsForm.patchValue({
        sizeBuses: this.boondock.sizeBuses,
        sizeCars: this.boondock.sizeCars,
      });

      this.boondockAccommodationsForm.controls.amenities.clear();
      this.boondock.amenities?.forEach((amenity) => {
        this.boondockAccommodationsForm.controls.amenities.push(amenityFormBuilder(this.fb, amenity));
      });

      this.amenitiesForm.controls.amenities.clear();
      this.boondock.amenities?.forEach((amenity) => {
        this.amenitiesForm.controls.amenities.push(amenityFormBuilder(this.fb, amenity));
      });

      this.activitiesForm.clear();
      this.boondock.activities?.forEach((activity) => {
        this.activitiesForm.push(activityFormBuilder(this.fb, activity));
      });

      this.amenitiesForm.controls.signalReception.clear();
      this.boondock.signalReception?.forEach((signal) => {
        this.amenitiesForm.controls.signalReception.push(signalReceptionFormBuilder(this.fb, signal));
      });
    }
  }

  saveRvAccess() {
    if (this.rvAccessForm.valid) {
      // Update the user record with the following data
      const vehicles = formatVehiclesFormValue(this.rvAccessForm.controls.vehicles);
      this.siteFacade.updateRvAccess(vehicles);
    } else {
      this.alertFacade.display({
        title: 'Form Incomplete',
        body: 'Check the form for errors before continuing.',
        level: 'warning',
      });
    }
  }

  saveNoiseAndPrivacy() {
    if (this.privacyFormComponent) {
      this.privacyFormComponent.validate();
    }
    this.listingNoiseComponent.validate();
    if (this.accommodationsForm.valid && this.privacyForm.valid && this.noiseForm.valid) {
      const noise = ListingForm.formatNoiseFormValue(this.noiseForm);
      const privacy = formatPrivacyFormValue(this.privacyForm);
      if (this.site || this.listing?.id) {
        // Update the site record with the following data
        this.siteFacade.updateNoiseAndPrivacy(noise, privacy);
      } else if (this.boondock?.id) {
        this.boondock = {
          ...this.boondock,
          noise,
          privacy,
        };
        this.boondockFacade.update(this.boondock);
      }
    } else {
      this.alertFacade.display({
        title: 'Form Incomplete',
        body: 'Check the form for errors before continuing.',
        level: 'warning',
      });
    }
  }

  saveAccommodations() {
    if (this.listingAccommodationsComponent) {
      this.listingAccommodationsComponent.validate();
    }
    if (this.accommodationsForm.valid) {
      const amenities = this.accommodationsForm?.controls.amenities.controls.map((amenity) =>
        formatAmenityFormValue(amenity),
      );
      const maxWidth = this.accommodationsForm?.controls.maxWidth?.value || undefined;
      const maxLength = this.accommodationsForm?.controls.maxLength?.value || undefined;
      const maxHeight = this.accommodationsForm?.controls.maxHeight?.value || undefined;
      const maxGuests = this.accommodationsForm?.controls.maxGuests?.value || 1;

      // Update the user record with the following data
      this.siteFacade.updateAccommodations({ amenities, maxWidth, maxLength, maxHeight, maxGuests });
    } else {
      this.alertFacade.display({
        title: 'Form Incomplete',
        body: 'Check the form for errors before continuing.',
        level: 'warning',
      });
    }
  }

  saveBoondockAccommodations() {
    if (this.boondockAccommodationsForm.valid) {
      this.boondock = {
        ...this.boondock,
        amenities: this.boondockAccommodationsForm?.controls.amenities.controls.map((amenity) =>
          formatAmenityFormValue(amenity),
        ),
        sizeBuses: this.boondockAccommodationsForm?.controls.sizeBuses?.value || undefined,
        sizeCars: this.boondockAccommodationsForm?.controls.sizeCars?.value || undefined,
      };
      this.boondockFacade.update(this.boondock);
    } else {
      this.alertFacade.display({
        title: 'Form Incomplete',
        body: 'Check the form for errors before continuing.',
        level: 'warning',
      });
    }
  }

  saveAmenities() {
    if (this.amenitiesForm.valid && this.listing?.id) {
      const amenities = this.amenitiesForm?.controls?.amenities.controls.map((amenity) =>
        ListingForm.formatAmenityFormValue(amenity),
      ) as IAmenity[];
      const extras =
        this.amenitiesForm?.controls?.extras.controls.map((extra) => formatSiteExtraFormValue(extra)) ?? [];
      const signalReception = this.amenitiesForm?.controls?.signalReception.controls.map((signal) =>
        formatSignalReceptionFormValue(signal),
      );
      this.siteFacade.updateAmenitiesAndExtras(this.listing.id, { amenities, extras, signalReception });
    } else if (this.amenitiesForm.valid && this.boondock?.id) {
      const amenities = this.amenitiesForm?.controls?.amenities.controls.map((amenity) =>
        ListingForm.formatAmenityFormValue(amenity),
      ) as IAmenity[];
      const signalReception = this.amenitiesForm?.controls?.signalReception.controls.map((signal) =>
        formatSignalReceptionFormValue(signal),
      );
      this.boondock = { ...this.boondock, amenities, signalReception };
      this.boondockFacade.update(this.boondock);
    } else {
      this.alertFacade.display({
        title: 'Form Incomplete',
        body: 'Check the form for errors before continuing.',
        level: 'warning',
      });
    }
  }

  saveActivities() {
    this.amenitiesComponent.validate();

    const activities = this.activities?.controls?.map((activity) =>
      ListingForm.formatActivityFormValue(activity),
    ) as IActivity[];

    if (this.site || this.listing?.id) {
      this.siteFacade.updateActivities(activities);
    }
    if (this.boondock?.id) {
      this.boondock = { ...this.boondock, activities };
      this.boondockFacade.update(this.boondock);
    }
  }

  toggleTentOnly() {
    if (this.tentOnly) {
      this.tentOnly.setValue(!this.tentOnly.value);
    }
  }
}
